import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, DollarSign, TrendingUp, MapPin, Sparkles, ArrowUpRight, ArrowRight, Layers, RefreshCw, Bell, CheckCircle2, AlertTriangle } from 'lucide-react';
import { usePlatformStore } from '@/lib/store';
import { StatCard, StatusBadge, PlanBadge, Spinner } from '@/components/shared/UIComponents';
import { formatCurrency, formatCompact, formatRelative } from '@/utils/helpers';
import { triggerUsageSync, fetchSyncLog, fetchNotifications, markNotificationRead, markAllNotificationsRead, fetchCronSchedule } from '@/lib/api';
import type { TenantPlan, PlanName } from '@/types';
import type { SyncLogEntry, PlatformNotification } from '@/lib/api';

export function OverviewPage() {
  const { tenants, plans, stats, loading, loadTenants, loadStats, loadPlans } = usePlatformStore();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [notifications, setNotifications] = useState<PlatformNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [syncScheduleLabel, setSyncScheduleLabel] = useState('daily at 2:00 AM UTC');

  useEffect(() => { loadTenants(); loadStats(); loadPlans(); }, [loadTenants, loadStats, loadPlans]);
  useEffect(() => {
    fetchSyncLog(5).then(setSyncLog).catch(() => {});
    fetchNotifications(true).then(setNotifications).catch(() => {});
    fetchCronSchedule().then(data => {
      const parts = data.schedule.split(' ');
      const h = parseInt(parts[1]) || 0;
      const m = parseInt(parts[0]) || 0;
      setSyncScheduleLabel(`daily at ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} UTC`);
    }).catch(() => {});
  }, []);
  const recent = tenants.slice(0, 6);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await triggerUsageSync();
      setSyncResult(`Sync ${res.sync_status}: ${res.synced} synced, ${res.failed} failed`);
      loadStats(); // refresh stats
      fetchSyncLog(5).then(setSyncLog).catch(() => {});
      fetchNotifications(true).then(setNotifications).catch(() => {});
    } catch (err: any) {
      setSyncResult(`Sync error: ${err.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 5000);
    }
  };

  const handleDismissNotification = async (id: string) => {
    await markNotificationRead(id).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleDismissAll = async () => {
    await markAllNotificationsRead().catch(() => {});
    setNotifications([]);
  };

  // Dynamic plan metrics from DB plans
  const planMetrics = useMemo(() => {
    const activePlans = plans.filter(p => p.is_active).sort((a, b) => a.rank - b.rank);
    return activePlans.map(plan => {
      const planTenants = tenants.filter(t => (t.plan as TenantPlan)?.id === plan.id);
      const count = planTenants.length;
      const totalSeats = planTenants.reduce((s, t) => s + t.current_user_count, 0);
      const mrr = planTenants.reduce((s, t) => s + (plan.price_per_seat_monthly * t.current_user_count), 0);
      return { plan, count, totalSeats, mrr };
    });
  }, [plans, tenants]);

  const totalPlanMrr = planMetrics.reduce((s, m) => s + m.mrr, 0);
  const totalPlanSeats = planMetrics.reduce((s, m) => s + m.totalSeats, 0);

  if (loading && !stats) return <div className="flex items-center justify-center py-32"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Platform Overview</h1>
        <p className="text-surface-500 mt-1">Monitor your white-label SaaS platform at a glance</p>
      </div>

      {/* Sync Failure Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map(n => (
            <div key={n.id} className={`flex items-start gap-3 p-3 rounded-xl border ${
              n.severity === 'error' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
              n.severity === 'warning' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' :
              'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
            }`}>
              <AlertTriangle size={16} className={
                n.severity === 'error' ? 'text-red-500 mt-0.5' :
                n.severity === 'warning' ? 'text-amber-500 mt-0.5' :
                'text-blue-500 mt-0.5'
              } />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-900">{n.title}</p>
                <p className="text-xs text-surface-600 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-surface-400 mt-1">{formatRelative(n.created_at)}</p>
              </div>
              <button onClick={() => handleDismissNotification(n.id)} className="text-surface-400 hover:text-surface-600 p-1">
                <CheckCircle2 size={14} />
              </button>
            </div>
          ))}
          {notifications.length > 1 && (
            <button onClick={handleDismissAll} className="text-xs text-surface-500 hover:text-surface-700 underline">
              Dismiss all notifications
            </button>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Tenants" value={stats?.total_tenants ?? 0} change={stats?.growth_rate ? `+${stats.growth_rate}%` : 'No prior data'} changeType={stats?.growth_rate ? 'positive' : 'neutral'} icon={<Building2 size={18} />} delay={0} />
        <StatCard label="End Users" value={formatCompact(stats?.total_end_users ?? 0)} change={`${stats?.total_end_users ?? 0} total`} changeType="neutral" icon={<Users size={18} />} delay={50} />
        <StatCard label="Monthly Revenue" value={formatCurrency(stats?.mrr ?? 0)} change={`${formatCurrency(stats?.arr ?? 0)} ARR`} changeType="neutral" icon={<DollarSign size={18} />} delay={100} />
        <StatCard label="Growth Rate" value={stats?.growth_rate ? `${stats.growth_rate}%` : '—'} change="vs last month" changeType="neutral" icon={<TrendingUp size={18} />} delay={150} />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MiniStat icon={<Building2 size={14} />} color="emerald" label="Active Tenants" value={stats?.active_tenants ?? 0} sub={`${stats?.trial_tenants ?? 0} in trial`} delay={200} />
        <MiniStat icon={<MapPin size={14} />} color="blue" label="Trips This Month" value={formatCompact(stats?.trips_this_month ?? 0)} sub="across all tenants" delay={250} />
        <MiniStat icon={<Sparkles size={14} />} color="violet" label="AI Requests" value={formatCompact(stats?.ai_requests_this_month ?? 0)} sub="this billing cycle" delay={300} />
      </div>

      {/* Buddy Trip moderation (abuse rollup + audit log) moved to its own
          page — see /admin/buddy-abuse (BuddyAbusePage). */}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-surface-900">Revenue Trend</h3>
              <p className="text-xs text-surface-500 mt-0.5">Monthly recurring revenue</p>
            </div>
            <div className="flex gap-1">
              {['6M', '1Y', 'All'].map(p => (
                <button key={p} className="px-2.5 py-1 text-xs rounded-md bg-surface-100 text-surface-600 hover:text-surface-900 hover:bg-surface-200 transition-colors">{p}</button>
              ))}
            </div>
          </div>
          <div className="h-56 flex items-center justify-center rounded-xl bg-surface-50 border border-dashed border-surface-300">
            <div className="text-center">
              <DollarSign size={24} className="mx-auto text-surface-400 mb-2" />
              <p className="text-sm text-surface-500">Revenue chart populates with Stripe data</p>
              <p className="text-xs text-surface-400 mt-1">Connect Stripe to see MRR trends</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-surface-900">Plan Distribution</h3>
              <p className="text-xs text-surface-500 mt-0.5">{planMetrics.length} active plan{planMetrics.length !== 1 ? 's' : ''} · {totalPlanSeats} total seats</p>
            </div>
            <Link to="/admin/plan-builder" className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-300 transition-colors">
              Manage Plans <ArrowRight size={12} className="inline ml-0.5" />
            </Link>
          </div>
          {planMetrics.length === 0 ? (
            <div className="py-8 text-center">
              <Layers size={24} className="mx-auto text-surface-400 mb-2" />
              <p className="text-sm text-surface-500">No active plans. Create plans in Plan Builder.</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {planMetrics.map(({ plan, count, totalSeats, mrr }) => {
                  const pct = Math.round((count / Math.max(tenants.length, 1)) * 100);
                  return (
                    <div key={plan.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: plan.color }} />
                          <span className="text-sm text-surface-700">{plan.display_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-surface-500 tabular-nums">{count} tenant{count !== 1 ? 's' : ''} · {totalSeats} seats</span>
                          {mrr > 0 && <span className="text-xs font-semibold text-surface-700 tabular-nums">{formatCurrency(mrr)}/mo</span>}
                        </div>
                      </div>
                      <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: plan.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-overlay/[0.04] flex items-center justify-between">
                <span className="text-xs text-surface-500">Total Plan Revenue</span>
                <span className="text-sm font-bold text-surface-900 tabular-nums">{formatCurrency(totalPlanMrr)}/mo</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sync Pipeline Control */}
      <div className="glass-panel p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-surface-900">Usage Metrics Sync</h3>
            <p className="text-xs text-surface-500 mt-0.5">Syncs B2C data (users, trips, AI requests, storage) to the Platform DB {syncScheduleLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {syncResult && (
              <span className={`text-xs px-2.5 py-1 rounded-full ${
                syncResult.includes('success') ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                syncResult.includes('error') ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
              }`}>
                {syncResult}
              </span>
            )}
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>

        {/* Recent sync history */}
        {syncLog.length > 0 && (
          <div className="border-t border-surface-200 pt-3 mt-2">
            <p className="text-xs font-medium text-surface-500 mb-2">Recent Syncs</p>
            <div className="space-y-1.5">
              {syncLog.map(log => (
                <div key={log.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      log.status === 'success' ? 'bg-emerald-500' :
                      log.status === 'partial' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                    <span className="text-surface-600">
                      {log.tenants_synced} synced{log.tenants_failed > 0 && `, ${log.tenants_failed} failed`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-surface-400">
                    <span className="capitalize">{log.triggered_by}</span>
                    <span>{formatRelative(log.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SQL Sync Instructions */}
        <div className="border-t border-surface-200 pt-3 mt-3">
          <p className="text-xs font-medium text-surface-500 mb-1">Manual SQL Sync</p>
          <p className="text-[11px] text-surface-400">Run in Supabase SQL Editor:</p>
          <code className="block mt-1 text-[11px] bg-surface-100 dark:bg-surface-800 rounded-lg px-3 py-2 text-surface-600 dark:text-surface-300 font-mono">
            SELECT sync_usage_metrics_now();
          </code>
          <p className="text-[11px] text-surface-400 mt-1">Or for a specific tenant:</p>
          <code className="block mt-1 text-[11px] bg-surface-100 dark:bg-surface-800 rounded-lg px-3 py-2 text-surface-600 dark:text-surface-300 font-mono">
            SELECT sync_usage_metrics_now('tenant-uuid-here');
          </code>
        </div>
      </div>

      {/* Recent Tenants */}
      <div className="glass-panel overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-surface-200">
          <div>
            <h3 className="text-sm font-semibold text-surface-900">Recent Tenants</h3>
            <p className="text-xs text-surface-500 mt-0.5">Latest organisations on the platform</p>
          </div>
          <Link to="/admin/tenants" className="btn-ghost text-sm">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell">Organisation</th>
                <th className="table-header-cell">Plan</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Users</th>
                <th className="table-header-cell">Domain</th>
                <th className="table-header-cell">Created</th>
                <th className="table-header-cell w-10"></th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-surface-500">No tenants yet. Create your first tenant to get started.</td></tr>
              ) : recent.map(t => (
                <tr key={t.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shrink-0 shadow-sm ring-1 ring-slate-500/30">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      </div>
                      <div>
                        <div className="font-medium text-surface-900">{t.name}</div>
                        <div className="text-xs text-surface-500">{t.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell"><PlanBadge plan={(t.plan as TenantPlan)?.name || 'free'} label={(t.plan as TenantPlan)?.display_name} /></td>
                  <td className="table-cell"><StatusBadge status={t.status} /></td>
                  <td className="table-cell">
                    <span className="text-surface-800">{t.current_user_count}</span>
                    <span className="text-surface-400">/{t.max_users || '∞'}</span>
                  </td>
                  <td className="table-cell"><span className="text-xs font-mono text-surface-500">{t.subdomain}</span></td>
                  <td className="table-cell text-surface-500 text-xs">{formatRelative(t.created_at)}</td>
                  <td className="table-cell">
                    <Link to={`/admin/tenants/${t.id}`} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors inline-flex">
                      <ArrowUpRight size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, color, label, value, sub, delay }: {
  icon: ReactNode; color: string; label: string; value: string | number; sub: string; delay: number;
}) {
  const styles: Record<string, { bg: string; text: string }> = {
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400' },
    blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400' },
    violet: { bg: 'bg-violet-50 dark:bg-violet-900/20', text: 'text-violet-600 dark:text-violet-400' },
  };
  const s = styles[color] || styles.blue;
  return (
    <div className="glass-panel p-5 animate-slide-up opacity-0" style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-1.5 rounded-lg ${s.bg} ${s.text}`}>{icon}</div>
        <span className="text-sm text-surface-500">{label}</span>
      </div>
      <div className="text-xl font-bold text-surface-900">{value}</div>
      <div className="text-xs text-surface-500 mt-1">{sub}</div>
    </div>
  );
}
